import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ArchiveStatus } from 'src/app/billers/models/archive-status';
import { CustomerBiller, LiabilitySource } from 'src/app/billers/models/model';
import { LIABILITY_SOURCE_MAP } from '../../../billers/configs/liability-source-map.config';
import { AnalyticsEvent, AnalyticsService } from '../../../core/services/analytics-service';
import { DialogService } from '../../../core/services/dialog.service';
import { HandsetService } from '../../../core/services/handset.service';
import { PdfService } from '../../../core/services/pdf.service';
import { ToolbarService } from '../../../core/services/toolbar.service';
import { AddToCartEventSource } from '../../../pay/models/add-to-cart-event-source.enum';
import { PaymentStatus } from '../../../pay/models/payment-status.model';
import { BasePayment } from '../../../pay/models/payment.model';
import { PaymentService } from '../../../pay/services/payment.service';
import {
  FillAmountDialogComponent,
  FillAmountDialogResult,
} from '../../components/fill-amount-dialog/fill-amount-dialog.component';
import {
  FillDescriptionDialogComponent,
} from '../../components/fill-description-dialog/fill-description-dialog.component';
import { FillDescriptionDialogResult } from '../../models/liability-description.model';
import { Liability } from '../../models/liability.model';
import { ILiabilityPayment, LiabilityStatus } from '../../models/model';
import { LiabilityApiService } from '../../services/liability-api.service';
import { DEFAULT_PATTERN, DEFAULT_PATTERN_WITH_TIME } from '../../../shared/configs/date-config';
import { LiabilityService } from '../../services/liability.service';
import { getReturnUrlFromRoute } from '../../../shared/utils/return-url.utils';
import { TagListDialogComponent } from '../../../billers/components/tag-list-dialog/tag-list-dialog.component';
import { MAX_WIDTH_90, WIDTH_360 } from '../../../shared/configs/dialog-config';
import { AppConfig } from '../../../core/services/app-config.service';
import { CustomerBillerStoreService } from '../../../billers/services/customer-biller-store.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderButtonType } from '../../../shared/models/loader-button-type.enum';

@Component({
standalone: false,
  selector: 'app-liability-details',
  styleUrls: ['./liability-details.component.scss'],
  templateUrl: './liability-details.component.html',
})
export class LiabilityDetailsComponent implements OnInit, OnDestroy {
  public defaultPatternWithTime: string = DEFAULT_PATTERN_WITH_TIME;
  public defaultPattern: string = DEFAULT_PATTERN;
  public liability?: Liability;
  public liabilitySourceMap = LIABILITY_SOURCE_MAP;
  public LiabilitySource = LiabilitySource;
  public archiveStatus = ArchiveStatus;
  public paymentStatus = PaymentStatus;
  public loadingError: boolean;
  public amountLoading: boolean;
  public unsubscribe = new Subject();
  public loading: boolean;
  public startedPayment: BasePayment;
  public showAddToCart: boolean;
  public addingToCart = false;
  public additionalStatusDescription: string;
  private returnUrl: string;
  private state: any;
  private customerBiller: CustomerBiller;
  public pdfUrl = null;
  public documentPreviewVisible = false;
  public liabilityOperator = null;

  constructor(
    public pdfService: PdfService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private paymentService: PaymentService,
    private toolbar: ToolbarService,
    private dialog: DialogService,
    public handsetService: HandsetService,
    private analyticsService: AnalyticsService,
    private liabilityApiService: LiabilityApiService,
    private liabilityService: LiabilityService,
    public appConfig: AppConfig,
    private dialogService: DialogService,
    private customerBillerStoreService: CustomerBillerStoreService,
    private snackbar: MatSnackBar) {
    this.toolbar.init(this.activatedRoute);
    this.toolbar.onBackClicked(() => this.router.navigate(['/liabilities']));
    this.loadData();


  }

  public ngOnInit(): void {

    this.returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
    let status = this.activatedRoute.snapshot.queryParamMap.get('status');
    let query = this.activatedRoute.snapshot.queryParamMap.get('query');
    this.state = {
      status: status,
      query: query,
    };


    this.paymentService
      .getStartedPayment()
      .pipe(
        map((payment) => {
          this.startedPayment = payment;
        }),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
    this.pdfService.clearConfirmationUrl(this.pdfUrl);
  }

  public loadData(): void {
    this.loading = true;

    this.activatedRoute.paramMap
      .pipe(
        map((paramMap) => paramMap.get('id')),
        switchMap((id) => this.liabilityService.getLiabilityWithSubscription(id)),
        takeUntil(this.unsubscribe),
        tap((liability: Liability) => {
          this.setupLiability(liability);
        }),
        tap(() => {
          this.loading = false;
          this.loadingError = false;
          this.amountLoading = false;
        }),
        switchMap((value) => this.customerBillerStoreService.fetchCustomerBiller(value.customerBillerId)),
        tap((value: CustomerBiller) => this.customerBiller = value),
        catchError(async (err) => {
          this.loading = false;
          this.loadingError = true;
          this.amountLoading = false;
          if (err.status === 403) {
            await this.router.navigate(['/liabilities']);
          }
          throw err;
        }),
      )
      .subscribe();
  }

  private setupLiability(liability: Liability) {
    this.liability = liability;
    if (!this.liability.verified) {
      this.liability.status = LiabilityStatus.IN_VERIFICATION;
    }
    this.setUpOperator();
    this.checkCanShowAddToCart();
    this.setAdditionalStatusDescription();
  }

  private refreshLiability(liability: Liability) {
    this.liability = liability;
    if (!this.liability.verified) {
      this.liability.status = LiabilityStatus.IN_VERIFICATION;
    }
    this.setUpOperator();
    this.checkCanShowAddToCart();
    this.setAdditionalStatusDescription();
  }

  public editAmount(): void {
    this.dialog
      .open(FillAmountDialogComponent, {
        closeOnNavigation: true,
        data: this.liability,
        disableClose: true,
        maxWidth: this.handsetService.isMobile ? '100vw' : '90vw',
      })
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        tap(() => {
          this.analyticsService.trackEvent(new AnalyticsEvent('Editliabilityamount'));
          this.amountLoading = true;
        }),
        switchMap((result: FillAmountDialogResult) =>
          this.liabilityApiService.updateAmount(this.liability.id, result.amount),
        ),
        tap((liability) => {
          this.setupLiability(liability);
          this.amountLoading = false;
        }),
      )
      .subscribe();
  }

  public editDescription(): void {
    this.dialog
      .open(FillDescriptionDialogComponent, {
        closeOnNavigation: true,
        data: this.liability,
        disableClose: true,
        maxWidth: this.handsetService.isMobile ? '100vw' : '90vw',
      })
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        switchMap((result: FillDescriptionDialogResult) =>
          this.liabilityApiService.updateDescription(this.liability.id, result.description).pipe(
            tap(() => {
              this.analyticsService.trackEvent(new AnalyticsEvent('EditLiabilityDescription'));
              this.liability.description = result.description;
            }),
          ),
        ),
      )
      .subscribe();
  }

  public async onInitPayment(): Promise<any> {
    if (!this.liability?.amount) {
      this.dialog
        .open(FillAmountDialogComponent, {
          closeOnNavigation: true,
          data: this.liability,
          disableClose: true,
        })
        .afterClosed()
        .pipe(
          filter((result) => !!result),
          tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('Editliabilityamount'))),
          switchMap((result: FillAmountDialogResult) =>
            this.liabilityApiService.updateAmount(this.liability.id, result.amount),
          ),
          switchMap(() => this.onInitPayment()),
        )
        .subscribe();
    } else {
      this.startedPayment !== null
        ? this.handleStartedPayment(this.startedPayment)
        : await this.findPaymentForCurrentLiability();
    }
  }

  public handleStartedPayment(startedPayment: BasePayment): void {
    const startedPaymentId = startedPayment.id;
    this.router.navigate(['otherPaymentInfo'], {
      queryParams: { startedPaymentId },
      relativeTo: this.activatedRoute,
    });
  }

  public async findPaymentForCurrentLiability(): Promise<void> {
    this.addingToCart = true;

    const response = await this.paymentService.findPaymentIdByLiabilityId(this.liability.id).toPromise();
    if (response.paymentId) {
      await this.router.navigate(['/liabilities/pay', response.paymentId], {
        queryParams: {
          returnUrl: this.getReturnUrl(),
        },
      });
    } else {
      this.analyticsService.trackEvent(
        new AnalyticsEvent('START_PAYMENT', {
          fromChecklist: this.activatedRoute.snapshot.queryParamMap.get('fromChecklist'),
        }),
      );
      await this.paymentService
        .initPayment([this.liability.id], AddToCartEventSource.LIABILITY_DETAILS, this.getReturnUrl())
        .pipe(finalize(() => (this.addingToCart = false)))
        .toPromise();
    }
  }

  public goBack(): void {
    if (this.returnUrl) {
      if (this.state) {
        this.router.navigateByUrl(this.returnUrl, {
          state: this.state,
        });
      } else {
        this.router.navigateByUrl(this.returnUrl);
      }
    } else {
      this.router.navigateByUrl('/liabilities/all');

    }
  }

  public previewConfirmation(payment: ILiabilityPayment): void {
    if (this.pdfUrl) {
      return;
    }

    payment.confirmationLoading = true;

    this.pdfService
      .getConfirmationUrl(payment.id, this.liability.id)
      .pipe(
        tap((url: string) => {
          this.pdfUrl = url;
          this.documentPreviewVisible = true;
        }),
        finalize(() => (payment.confirmationLoading = false)),
      )
      .subscribe();
  }

  private checkCanShowAddToCart(): void {
    const statuses = [
      LiabilityStatus.NOT_PAID,
      LiabilityStatus.OVERDUE,
      LiabilityStatus.SCHEDULED_NOT_PAID,
      LiabilityStatus.UNKNOWN,
    ];
    this.showAddToCart =
      statuses.includes(this.liability.status) &&
      (this.liability.payable || (!this.liability.payable && !this.liability.amount)) &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED;
  }

  private setAdditionalStatusDescription(): void {
    if (this.liability.source === LiabilitySource.DELEGATED) {
      this.additionalStatusDescription = this.buildStatusDescription();
    }
  }

  private buildStatusDescription(): string {
    return this.liability.delegationData?.ownerDisplayName
      ? `Opłaty przekazywane przez użytkownika <span class="nowrap">${this.liability.delegationData.ownerDisplayName}</span>`
      : 'Opłaty przekazywane przez innego użytkownika';
  }

  public getReturnUrl(): string {
    return getReturnUrlFromRoute(this.activatedRoute);
  }

  private setUpOperator() {
    this.liabilityOperator = this.liability.title?.split(':')[1]?.trim();
  }

  public addTagClick() {
    if (this.appConfig.config.applicationReadOnlyMode) {
      this.dialogService.openReadOnlyInfoDialog();
    } else {
      this.dialogService
        .open(TagListDialogComponent, { data: this.customerBiller, maxWidth: MAX_WIDTH_90, width: WIDTH_360 })
        .afterClosed()
        .pipe(
          filter((closed) => closed),
          tap(() => this.customerBillerStoreService.refreshConfiguration()),

          switchMap(() => this.liabilityService.getLiabilityWithSubscription(this.liability.id)),
          takeUntil(this.unsubscribe),
          tap((liability: Liability) => {
            this.setupLiability(liability);
          }),
          tap(() => {
            this.loading = false;
            this.loadingError = false;
            this.amountLoading = false;
          }),
          switchMap((value) => this.customerBillerStoreService.fetchCustomerBiller(value.customerBillerId)),
          tap((value: CustomerBiller) => this.customerBiller = value),
          catchError(async (err) => {
            this.loading = false;
            this.loadingError = true;
            this.amountLoading = false;
            if (err.status === 403) {
              await this.router.navigate(['/liabilities']);
            }
            throw err;
          }),
          // tap(() => this.snackbar.open('Etykiety zostały pomyślnie zapisane.', null, { duration: DURATION_5S })),
        )
        .subscribe();
    }
  }

  protected readonly LoaderButtonType = LoaderButtonType;
}
